import { useEffect, useState } from "react";
import { getProperty, safeParseJSON } from "../../../utils/jsonValidator";

export const useManageComponentFlow = (flowInEdition, setOpenModal, setValeComponentFlowInEdition) => {
    const [orders, setOrders] = useState({ nextOrder: "01", listOrders: ["01"] });
    const [itemEditComponentFlow, setitemEditComponentFlow] = useState({});
    const [flowComponent, setflowComponent] = useState([]);
    const [typeAction, settypeAction] = useState("");

    useEffect(() => {
        const component = safeParseJSON(flowInEdition?.component);
        setflowComponent(getProperty(component, "flow_component") || []);
    }, [flowInEdition]);

    //#region HELPERS
    const splitOrder = (order) => order.split(".").map(Number);

    const compareOrders = (a, b, missingAsZero = false) => {
        const aParts = splitOrder(a);
        const bParts = splitOrder(b);
        const len = Math.max(aParts.length, bParts.length);
        for (let i = 0; i < len; i++) {
            const aVal = aParts[i] ?? (missingAsZero ? 0 : -1);
            const bVal = bParts[i] ?? (missingAsZero ? 0 : -1);
            if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
    };

    const getNextOrder = (data, baseOrder = "") => {
        const prefix = baseOrder ? `${baseOrder}.` : "";
        const baseLevel = baseOrder ? baseOrder.split(".").length + 1 : 1;

        const siblings = data
            .map((i) => i.order)
            .filter((o) => o.startsWith(prefix) && o.split(".").length === baseLevel)
            .sort((a, b) => compareOrders(a, b));

        const lastNum = siblings.length
            ? parseInt(siblings[siblings.length - 1].split(".").pop())
            : 0;

        const next = (lastNum + 1).toString().padStart(2, "0");
        return {
            nextOrder: prefix + next,
            listOrders: [...siblings, prefix + next],
        };
    };

    const incrementOrder = (order, baseParts) => {
        const parts = order.split(".");
        const level = baseParts.length - 1;
        const length = parts[level].length;
        parts[level] = (parseInt(parts[level]) + 1).toString().padStart(length, "0");
        return parts.join(".");
    };

    const isAffected = (currentOrder, baseParts) => {
        const parts = currentOrder.split(".");
        if (parts.length < baseParts.length) return false;
        for (let i = 0; i < baseParts.length - 1; i++) {
            if (baseParts[i] !== parts[i]) return false;
        }
        return parseInt(parts[baseParts.length - 1]) >= parseInt(baseParts[baseParts.length - 1]);
    };

    // Reorder
    const deleteAndReorder = (components, orderToDelete) => {
        const baseParts = orderToDelete.split(".");
        const level = baseParts.length - 1;
        const targetIndex = parseInt(baseParts[level]);
        const parentPrefix = baseParts.slice(0, level).join(".");

        const filtered = components.filter(
            (comp) => comp.order !== orderToDelete && !comp.order.startsWith(orderToDelete + ".")
        );

        const updated = filtered.map((comp) => {
            const currentParts = comp.order.split(".");
            if (currentParts.length <= level) return comp;
            const isSameParent = parentPrefix === currentParts.slice(0, level).join(".");
            const currentIndex = parseInt(currentParts[level]);
            if (isSameParent && currentIndex > targetIndex) {
                currentParts[level] = (currentIndex - 1)
                    .toString()
                    .padStart(currentParts[level].length, "0");
                return { ...comp, order: currentParts.join(".") };
            }
            return comp;
        });

        return updated.sort((a, b) => compareOrders(a.order, b.order));
    };

    const insertAndReorder = (components, newComponent) => {
        const baseParts = newComponent.order.split(".");
        const updated = components.map((comp) =>
            isAffected(comp.order, baseParts)
                ? { ...comp, order: incrementOrder(comp.order, baseParts) }
                : comp
        );
        updated.push(newComponent);
        return updated.sort((a, b) => compareOrders(a.order, b.order));
    };

    const moveAndReorder = (components, fromOrder, toOrder) => {
        if (fromOrder === toOrder) return components;

        let updated = [...components];
        const moveAfter = compareOrders(fromOrder, toOrder) < 0;

        const movingBlock = updated.filter(
            (comp) => comp.order === fromOrder || comp.order.startsWith(fromOrder + ".")
        );

        updated = updated.filter(
            (comp) => comp.order !== fromOrder && !comp.order.startsWith(fromOrder + ".")
        );

        const toParts = toOrder.split(".");
        const newParentPrefix = toParts.slice(0, -1).join(".");
        const newLevel = toParts.length;

        const siblings = updated
            .filter((c) => {
                const parts = c.order.split(".");
                return (
                    parts.length === newLevel &&
                    c.order.startsWith(newParentPrefix ? newParentPrefix + "." : "")
                );
            })
            .sort((a, b) => compareOrders(a.order, b.order));

        let insertIndex = siblings.findIndex((c) => c.order === toOrder);
        if (insertIndex === -1) insertIndex = siblings.length - 1;
        if (moveAfter) insertIndex++;

        const affected = siblings.slice(insertIndex);
        const basePrefix = newParentPrefix ? newParentPrefix + "." : "";
        const newOrderNum = (insertIndex + 1).toString().padStart(2, "0");
        const newBase = basePrefix + newOrderNum;

        const reordered = updated.map((comp) =>
            affected.some((a) => comp.order === a.order || comp.order.startsWith(a.order + "."))
                ? { ...comp, order: incrementOrder(comp.order, toParts) }
                : comp
        );

        const orderMap = {};
        const remappedBlock = movingBlock.map((comp) => {
            const relativePath = comp.order.substring(fromOrder.length);
            const newOrder = newBase + relativePath;
            orderMap[comp.order] = newOrder;
            return { ...comp, order: newOrder };
        });

        const deepReplaceOrders = (obj) => {
            if (typeof obj === "string") {
                let newStr = obj;
                Object.entries(orderMap).forEach(([oldOrd, newOrd]) => {
                    if (newStr === oldOrd) newStr = newOrd;
                    newStr = newStr.replace(new RegExp(`\\{\\[${oldOrd}\\]\\}`, "g"), `{[${newOrd}]}`);
                });
                return newStr;
            } else if (Array.isArray(obj)) {
                return obj.map((item) => deepReplaceOrders(item));
            } else if (typeof obj === "object" && obj !== null) {
                const newObj = {};
                for (const key in obj) {
                    newObj[key] = deepReplaceOrders(obj[key]);
                }
                return newObj;
            }
            return obj;
        };

        const remappedWithReferences = [...reordered, ...remappedBlock].map((comp) => {
            const updatedComp = { ...comp };
            if (updatedComp.input) {
                Object.entries(orderMap).forEach(([oldOrd, newOrd]) => {
                    if (updatedComp.input === oldOrd) updatedComp.input = newOrd;
                });
            }
            if (updatedComp.body) {
                updatedComp.body = deepReplaceOrders(updatedComp.body);
            }
            return updatedComp;
        });

        return remappedWithReferences.sort((a, b) => compareOrders(a.order, b.order));
    };

    // Select
    const getConditional = () =>
        flowComponent.filter((p) => p.type_component === "decision").map((p) => p.order);

    const getItemsWithChildrenExceptParent = (arr, x) => {
        const withChildren = arr.filter((item) =>
            arr.some((other) => other !== item && other.startsWith(item + "."))
        );
        const parent = x.includes(".") ? x.substring(0, x.lastIndexOf(".")) : null;
        return withChildren.filter((item) => item !== parent);
    };

    const getHigherOrders = (arr, x) => {
        if (!x) return [];

        const chl = getItemsWithChildrenExceptParent(arr, x);
        const conditionals = getConditional();
        const result = chl.filter((item) => !conditionals.includes(item));

        const nOrd = [getNextOrder(flowComponent, "").nextOrder];
        result.forEach((el) => nOrd.push(getNextOrder(flowComponent, el).nextOrder));
        conditionals.forEach((el) => {
            nOrd.push(getNextOrder(flowComponent, el + ".0").nextOrder);
            nOrd.push(getNextOrder(flowComponent, el + ".1").nextOrder);
        });

        const arr2 = [...arr, ...nOrd].filter((v) => compareOrders(v, x, true) > 0);
        return arr2.sort((a, b) => compareOrders(a, b, true));
    };

    const getLowerOrders = (arr, x) => {
        if (!x) return [];
        return arr.filter((v) => v && compareOrders(v, x) < 0).sort((a, b) => compareOrders(a, b));
    };
    //#endregion

    //#region ACTIONS
    const handleCreateComponetFlow = (ord) => {
        setOpenModal(true);
        settypeAction("ADD");
        setOrders(getNextOrder(flowComponent, ord));
    };

    const handleAddComponetFlow = (comp, fromOrder) => {
        if (typeAction === "EDIT") {
            const newFlow = moveAndReorder(flowComponent, fromOrder, comp.order);
            const dat = newFlow.find((F) => F.order === comp.order);
            if (comp?.input) dat.input = comp.input;
            setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
        } else {
            const newFlow = insertAndReorder(flowComponent, comp);
            setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
        }
    };

    const handleDeleteSubComponet = (ord) => {
        const newFlow = deleteAndReorder(flowComponent, ord);
        setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
    };

    const handleUpdateBodyComponetFlow = (comp, orderEdit) => {
        const newFlow = [...flowComponent];
        const index = newFlow.findIndex((p) => p.order === orderEdit);
        if (index === -1) return;
        newFlow[index].body = comp;
        setValeComponentFlowInEdition(JSON.stringify({ flow_component: newFlow }));
    };

    const handleEditComponet = (data) => {
        setOpenModal(true);
        settypeAction("EDIT");
        setitemEditComponentFlow(data);
        const filtered = [...new Set(flowComponent.filter((fc) => fc.order.startsWith(data.order)).map((i) => i.input))].filter(Boolean);
        const min = filtered.sort(compareOrders)[0];
        const ods = flowComponent.map((e) => e.order);
        const higherOrders = getHigherOrders(ods, min ?? "00").filter((fc) => !fc.startsWith(data.order));
        higherOrders.push(data.order);
        setOrders({ nextOrder: data.order, listOrders: higherOrders });
    };

    const getImputValid = (baseOrder) => {
        const arr = flowComponent.map((e) => e.order);
        const newArr = getLowerOrders(arr, baseOrder);
        newArr.push("-1");
        return newArr;
    };
    //#endregion

    return {
        orders,
        typeAction,
        itemEditComponentFlow,
        handleCreateComponetFlow,
        handleAddComponetFlow,
        handleDeleteSubComponet,
        handleUpdateBodyComponetFlow,
        handleEditComponet,
        getImputValid,
    };
};
